<template>
  <div class="space-y-2">
    <TransferInfo
      :duration="flightConnection.connectionDuration"
      :city="segment.arr.city"
      :is-rail-transfer="isRailTransfer"
      :rail-train-name="railTrainName"
      :rail-station-name="railStationName"
    />
    <div
      v-if="hasListWarning || hasSimpleWarning"
      class="bg-background-neutral-onblock space-y-2 rounded-xl p-3"
    >
      <ul v-if="hasListWarning" class="space-y-2 pl-2">
        <ListWarning v-if="flightConnection.isSelfMovingBaggage">
          {{
            t('warnings.baggageSelfTransition', {
              city: segment.arr.city,
              cityCode: segment.arr.airport,
            })
          }}
        </ListWarning>
        <ListWarning v-if="flightConnection.isRecheckRequired">
          {{ t('warnings.baggageReCheckIn') }}
        </ListWarning>
        <ListWarning v-if="flightConnection.hasNextDayDep">
          {{
            isNextSegmentRail
              ? t('warnings.nextDayDepartureRail')
              : t('warnings.nextDayDeparture')
          }}
        </ListWarning>
      </ul>
      <div v-if="hasSimpleWarning" class="space-y-2">
        <SimpleWarning
          v-if="flightConnection.isSelfTransfer"
          with-icon
          :color="NoteColor.green"
          @click="emit('onItemClick', faqConfig.faqItems.selfTransfer)"
        >
          {{ t('warnings.selfTransfer') }}
        </SimpleWarning>
        <SimpleWarning
          v-if="flightConnection.hasGuarantorAirline && !isRailTransfer"
          with-icon
          :color="NoteColor.green"
          @click="emit('onItemClick', faqConfig.faqItems.guarantorAirline)"
        >
          {{ t('warnings.guaranteedTransfer') }}
        </SimpleWarning>
        <SimpleWarning
          v-if="!isRailTransfer && flightConnection.hasDifferentTerminal"
          :color="NoteColor.orange"
        >
          {{ t('warnings.differentTerminals') }}
        </SimpleWarning>
        <SimpleWarning
          v-if="flightConnection.hasDifferentAirport"
          :color="NoteColor.orange"
        >
          {{ t('warnings.differentAirports') }}
        </SimpleWarning>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { flightConnectionState, ticketDetails } from 'shared';

  import {
    config as faqConfig,
    type types as faqTypes,
  } from '../../../faq-bottom-sheet';
  import TransferInfo from './TransferInfo.vue';
  import ListWarning from './ListWarning.vue';
  import SimpleWarning from './SimpleWarning.vue';
  import { NoteColor } from '../../types';

  const props = defineProps<{
    segment: ticketDetails.types.MappedSegment;
    nextSegment: ticketDetails.types.MappedSegment;
  }>();

  const emit = defineEmits<{
    (e: 'onItemClick', faqItemKey: faqTypes.FAQItemType): void;
  }>();

  const { t } = useI18n();

  const flightConnection = computed(() =>
    flightConnectionState.model.getFlightConnectionData(
      props.segment,
      props.nextSegment,
    ),
  );
  const isRailTransfer = computed(
    () =>
      ticketDetails.lib.isRailSegment(props.segment) ||
      ticketDetails.lib.isRailSegment(props.nextSegment),
  );

  const isNextSegmentRail = computed(() =>
    ticketDetails.lib.isRailSegment(props.nextSegment),
  );

  const railTrainName = computed(() => {
    if (!isNextSegmentRail.value) {
      return null;
    }
    return (
      props.nextSegment.equipment?.name ||
      props.nextSegment.airlineTitle ||
      (ticketDetails.lib.isHighSpeedRail(props.nextSegment)
        ? t('expressTrain')
        : t('train'))
    );
  });

  const railStationName = computed(() =>
    isNextSegmentRail.value ? props.nextSegment.dep.airportText : null,
  );

  const hasListWarning = computed(
    () =>
      flightConnection.value.isSelfMovingBaggage ||
      flightConnection.value.isRecheckRequired ||
      flightConnection.value.hasNextDayDep,
  );

  const hasSimpleWarning = computed(
    () =>
      flightConnection.value.isSelfTransfer ||
      flightConnection.value.hasGuarantorAirline ||
      flightConnection.value.hasDifferentAirport ||
      (!isRailTransfer.value && flightConnection.value.hasDifferentTerminal),
  );
</script>

<i18n src="../../config/locales.json"></i18n>
